import React, { FC, JSX, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Svg } from "@natera/material/lib/svg";

import { routes } from "@app/routing";
import timeSVG from "@assets/svg/icons/time.svg";
import documentSVG from "@assets/svg/icons/document.svg";
import dateDoneSVG from "@assets/svg/icons/dateDone.svg";
import linkArrowIcon from "@assets/svg/icons/link-arrow.svg";

import "./billingCenterHistoryItem.scss";
import { LinkHeap } from "@app/components";
import { HEAP_EVENTS } from "@app/provider/types";
import { UserContext } from "@app/provider";

const messages = defineMessages({
  billingCenterItemPayments: {
    id: "billingCenterItemPayments",
    defaultMessage: "Payments",
  },
  billingCenterItemStatements: {
    id: "billingCenterItemStatements",
    defaultMessage: "Statements",
  },
  billingCenterItemPaymentPlans: {
    id: "billingCenterItemPaymentPlans",
    defaultMessage: "Payment plans",
  },
});

export enum BillingCenterHistoryItemTypes {
  PAYMENTS = "Payments",
  STATEMENTS = "Statements",
  PAYMENT_PLANS = "Payment plans",
}

type BillingCenterHistoryItemProps = {
  type: BillingCenterHistoryItemTypes;
};

const BillingCenterHistoryItem: FC<BillingCenterHistoryItemProps> = ({
  type,
}) => {
  const intl = useIntl();
  const { uppUser } = useContext(UserContext);

  const handleRoute = (type: BillingCenterHistoryItemTypes): string => {
    switch (type) {
      case BillingCenterHistoryItemTypes.PAYMENTS:
        return routes.billingHistoryPayments;
      case BillingCenterHistoryItemTypes.STATEMENTS:
        return routes.billingHistoryStatements;
      case BillingCenterHistoryItemTypes.PAYMENT_PLANS:
        return routes.billingCenterPaymentPlanHistory;
    }
  };

  const handleSVG = (type: BillingCenterHistoryItemTypes): JSX.Element => {
    switch (type) {
      case BillingCenterHistoryItemTypes.PAYMENTS:
        return (
          <Svg
            className="billing-center-history-item__icon"
            content={timeSVG}
          />
        );
      case BillingCenterHistoryItemTypes.STATEMENTS:
        return (
          <Svg
            className="billing-center-history-item__icon"
            content={documentSVG}
          />
        );
      case BillingCenterHistoryItemTypes.PAYMENT_PLANS:
        return (
          <Svg
            className="billing-center-history-item__icon"
            content={dateDoneSVG}
          />
        );
    }
  };

  const handleTitle = (type: BillingCenterHistoryItemTypes): string => {
    switch (type) {
      case BillingCenterHistoryItemTypes.PAYMENTS:
        return intl.formatMessage(messages.billingCenterItemPayments);
      case BillingCenterHistoryItemTypes.STATEMENTS:
        return intl.formatMessage(messages.billingCenterItemStatements);
      case BillingCenterHistoryItemTypes.PAYMENT_PLANS:
        return intl.formatMessage(messages.billingCenterItemPaymentPlans);
    }
  };

  const handleHeapEvent = (type: BillingCenterHistoryItemTypes) => {
    switch (type) {
      case BillingCenterHistoryItemTypes.PAYMENTS:
        return HEAP_EVENTS.upp_click_history_payments;
      case BillingCenterHistoryItemTypes.PAYMENT_PLANS:
        return HEAP_EVENTS.upp_click_history_payment_plans;
    } //TO DO statements click event will be added post-mvp
  };

  return (
    <LinkHeap
      to={handleRoute(type)}
      className="billing-center-history-item"
      heapEventName={handleHeapEvent(type)}
      heapEventProps={{
        identity: uppUser?.patientUID,
      }}
    >
      {handleSVG(type)}
      <span className="billing-center-history-item__title">
        {handleTitle(type)}
      </span>
      <Svg
        className="billing-center-history-item__arrow"
        content={linkArrowIcon}
      />
    </LinkHeap>
  );
};

export default BillingCenterHistoryItem;
